import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapTrifold, ChartLineUp, Warning, Camera, Images, Leaf,
  ArrowRight, UploadSimple,
} from "@phosphor-icons/react";

/**
 * WeatherHubHero — polished landing band for /weather-almanac inspired
 * by nature-weather-app.emergent.host. Sits above the existing long-form
 * almanac article and gives newcomers immediate CTAs + feature overview.
 */
const HERO_BG = "https://images.unsplash.com/photo-1504608524841-42fe6f032b4b?auto=format&fit=crop&w=1920&q=80";

const FEATURES = [
  {
    key: "map", to: "/map",
    icon: MapTrifold, tint: "bg-orange-50 text-orange-700",
    title: "Interactive Map View",
    body: "Explore weather + nature observations on a live global map. Discover regional patterns, find nearby observations, and visualize environmental data by location.",
    cta: "Open map view",
  },
  {
    key: "data", to: "/climate-change",
    icon: ChartLineUp, tint: "bg-emerald-50 text-emerald-700",
    title: "Daily Weather Data",
    body: "Comprehensive weather metrics from NOAA + Open-Meteo including temperature, precipitation, wind, UV, humidity and pressure.",
    cta: "See climate hub",
  },
  {
    key: "alerts", to: "/notifications",
    icon: Warning, tint: "bg-red-50 text-red-700",
    title: "Weather Alerts + Articles",
    body: "Show weather alert news links by geography, cyclone tracks, heat warnings, floods and severe storm briefings — with embedded articles on top.",
    cta: "View alerts",
  },
  {
    key: "pictures", to: "/photopulse",
    icon: Camera, tint: "bg-sky-50 text-sky-700",
    title: "Weather Pictures + Videos",
    body: "Document weather phenomena — sunrises, storms, rainfall, unusual skies. Allow rich embeds and links with expanded text.",
    cta: "Browse PhotoPulse",
  },
  {
    key: "galleries", to: "/nature-museum",
    icon: Images, tint: "bg-indigo-50 text-indigo-700",
    title: "Community Galleries",
    body: "Explore community contributions, filter by category, and engage with weather and nature content across every continent.",
    cta: "Open galleries",
  },
  {
    key: "records", to: "/observations",
    icon: Leaf, tint: "bg-lime-50 text-lime-700",
    title: "Nature Records",
    body: "Upload pictures of plants, crops and nature with GPS data for precise environmental tracking that pairs with the weather archive.",
    cta: "Add a record",
  },
];

export default function WeatherHubHero() {
  return (
    <>
      {/* Full-bleed hero band */}
      <section
        className="relative overflow-hidden"
        data-testid="weather-hub-hero"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1620]/60 via-[#0A1620]/40 to-[#0A1620]/85" />
        <div className="relative max-w-6xl mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-16 md:pb-24">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-3 py-1 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-white/90 mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-[#E4A15C]" /> NaturePulse · Weather Almanac
            </div>
            <h1 className="font-serif-display text-5xl md:text-7xl leading-[0.95] tracking-tighter text-white">
              NaturePulse Weather Hub.
            </h1>
            <p className="mt-4 text-white/85 text-lg md:text-xl max-w-2xl">
              Track daily weather, share nature observations, and build a
              comprehensive environmental record with GPS precision. Every
              day, somewhere on Earth, history is being written.
            </p>
            <div className="mt-8 flex flex-wrap gap-3" data-testid="weather-hub-ctas">
              <Link
                to="/map"
                data-testid="weather-cta-map"
                className="inline-flex items-center gap-1.5 rounded-full bg-white text-[#0A1620] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase hover:bg-white/90 transition-colors"
              >
                <MapTrifold size={13} weight="fill" /> Explore map
              </Link>
              <Link
                to="/climate-change"
                data-testid="weather-cta-data"
                className="inline-flex items-center gap-1.5 rounded-full border border-white/40 text-white px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase hover:bg-white/10 transition-colors backdrop-blur"
              >
                <ChartLineUp size={13} weight="fill" /> Weather data
              </Link>
              <Link
                to="/#identify"
                data-testid="weather-cta-upload"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#E4572E] text-white px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase hover:bg-[#c94a25] transition-colors"
              >
                <UploadSimple size={13} weight="fill" /> Upload picture
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Track / Share / Preserve feature grid */}
      <section
        className="max-w-6xl mx-auto px-6 md:px-10 mt-20"
        data-testid="weather-feature-grid"
      >
        <div className="text-center">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#E4572E)]">
            Track · Share · Preserve
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl mt-2">
            A comprehensive platform for<br />environmental observation.
          </h2>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.key}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-15%" }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="rounded-2xl border border-black/10 bg-white p-6 hover:shadow-lg transition-shadow"
              data-testid={`weather-feature-${f.key}`}
            >
              <div className={`inline-flex w-10 h-10 rounded-xl items-center justify-center ${f.tint}`}>
                <f.icon size={18} weight="fill" />
              </div>
              <h3 className="mt-4 font-serif-display text-2xl leading-tight">{f.title}</h3>
              <p className="mt-2 text-[#3A3830] leading-relaxed text-sm">{f.body}</p>
              <Link
                to={f.to}
                className="mt-3 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#E4572E)] hover:underline"
              >
                {f.cta} <ArrowRight size={11} weight="bold" />
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
    </>
  );
}
